import React, { PropsWithChildren, cloneElement, isValidElement, ReactElement } from 'react';
import classNames from 'classnames';
import { BadgeColor, BadgeSize, BadgeShape, BadgeVariant } from './types';
import {
  badge_base_css,
  badge_icon_size_css,
  badge_size_css,
  getBadgeStyleByProps,
  rounded_badge_size_css,
} from './styles';
import { IconProps } from '../icons/generated';

export interface BadgeProps {
  className?: string;
  /** @default 'outlined' */
  variant?: BadgeVariant;
  /** @default 'pill' */
  shape?: BadgeShape;
  /** @default 'small' */
  size?: BadgeSize;
  /** @default 'gray' */
  color?: BadgeColor;
  /**
   * 라벨 좌측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 있더라도 BadgeColor에 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 없으면 기본으로 8이 지정됩니다.
   */
  leftIcon?: ReactElement<IconProps>;
  /**
   * 라벨 우측 영역에 렌더링 될 아이콘 요소
   *  - Icon 컴포넌트 color props가 있더라도 BadgeColor에 따라 currentColor가 지정됩니다.
   *  - Icon 컴포넌트 size props가 없으면 기본으로 8이 지정됩니다.
   */
  rightIcon?: ReactElement<IconProps>;
}

export const Badge = ({
  className,
  variant = 'outlined',
  shape = 'pill',
  size = 'small',
  color = 'gray',
  leftIcon,
  rightIcon,
  children,
}: PropsWithChildren<BadgeProps>) => {
  const _left_icon = isValidElement(leftIcon)
    ? cloneElement(leftIcon, { color: 'currentColor', size: leftIcon.props.size ?? badge_icon_size_css[size] })
    : null;
  const _right_icon = isValidElement(rightIcon)
    ? cloneElement(rightIcon, { color: 'currentColor', size: rightIcon.props.size ?? badge_icon_size_css[size] })
    : null;

  return (
    <span
      className={classNames('pds-badge', className)}
      css={[
        badge_base_css,
        badge_size_css[size],
        ...(shape === 'rounded' ? [rounded_badge_size_css[size]] : []),
        getBadgeStyleByProps(variant, color),
      ]}
    >
      {_left_icon}
      {children}
      {_right_icon}
    </span>
  );
};
